import { onError } from '@apollo/client/link/error';
import { datadogRum } from '@datadog/browser-rum';

import { sanitizeVariables } from './sanitizeVariables';

/**
 * Apollo link that forwards GraphQL and network errors to Datadog RUM.
 *
 * Reported:
 *   - `graphQLErrors` — a `data.errors` array on a 200 response. These are
 *     protocol-level errors (schema mismatches, parse failures, "not found"
 *     with GraphQL semantics, backend crashes surfaced through GraphQL).
 *     Business errors in our API use union response types (e.g. `... on
 *     WorkAssignmentBatchMutationErrorResponse`) and never reach this handler.
 *   - `networkError` — transport-level failures (4xx / 5xx / timeout / offline).
 *
 * Variables are sanitised to strip known PII fields before leaving the
 * browser; see `sanitizeVariables`.
 *
 * The link does not swallow errors — it re-emits them so downstream
 * components still receive their own `onError` callbacks.
 */
export const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
	const baseContext = {
		source: 'graphql',
		operation: operation.operationName,
		variables: sanitizeVariables(operation.variables),
	};

	if (graphQLErrors) {
		graphQLErrors.forEach((error) => {
			datadogRum.addError(error, {
				...baseContext,
				path: error.path,
				extensions: error.extensions,
			});
		});
	}

	if (networkError) {
		datadogRum.addError(networkError, {
			...baseContext,
			networkStatus: networkError.statusCode,
		});
	}
});
